import RoundButton from '@deltek/specification-client/core/Buttons/RoundButton'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import FilestackButton, {
    PickerFileMetadata,
    PickerResponse,
} from '@deltek/specification-client/core/Widgets/Filestack/FilestackButton'
import { Button } from '@material-ui/core'
import { Theme, makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import { processEnv } from 'components/configuration/environmentVariables'
import { ExecutionResult } from 'graphql'
import React from 'react'

const useStyles = makeStyles((theme: Theme) => ({
    container: { maxWidth: 368 },
    fullWidth: {
        width: '100%',
    },
    documentTitle: {
        color: '#5E5F61',
        textTransform: 'uppercase',
    },
    documentFileContainer: { height: 40, paddingLeft: theme.spacing(2) },
    documentFile: {
        ...theme.typography.h6,
    },
    noData: {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        flexDirection: 'column',
        fontWeight: 'normal',
        '& div button.MuiButton-contained': {
            height: 30,
            width: 80,
            padding: 0,
        },
        '& .MuiButton-label': {
            fontSize: pxToRem(12),
        },
    },
    workspaceButton: {
        borderRadius: 100,
        fontSize: pxToRem(12),
        backgroundColor: theme.palette.common.white,
        '&:hover': {
            backgroundColor: (props: ImportDocumentRowProps) =>
                props?.isBPImport ? theme.palette.common.white : '#33AEA6',
            color: (props: ImportDocumentRowProps) =>
                props?.isBPImport
                    ? theme.palette.common.black
                    : theme.palette.common.white,
            boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
        },
    },

    uploadBtnContainer: {
        paddingRight: theme.spacing(0.5),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
    },
    iconButton: {
        height: 36,
        width: 34,
        padding: 0,
        margin: 0,
        '& .MuiIconButton-label': {
            height: '100%',
        },
        '& .MuiIcon-root': {
            height: 'unset',
            width: 'unset',
        },
    },
    withBorder: {
        borderBottom: '1px solid #04325B',
    },
    withoutBorder: {
        borderBottom: '1px solid #FFFFFF',
    },
    withTopPadding: {
        paddingTop: theme.spacing(1),
    },
    fileStackWrapper: {
        width: (props: ImportDocumentRowStyleProps) =>
            props.isBPImport ? 'auto' : '100%',
    },
}))
interface ImportDocumentRowStyleProps {
    isBPImport?: boolean
}
interface ImportDocumentRowProps extends ImportDocumentRowStyleProps {
    handleFilestackLoading?: (filestackLoading: boolean | null) => void
    handlePickerDoneTransfer?: (value: PickerResponse) => void
    showGlobalSnackbar?: (options?: any) => Promise<ExecutionResult<any>>
    calledFrom?: string
}

export const ImportDocumentRow: React.FC<ImportDocumentRowProps> = ({
    handleFilestackLoading,
    handlePickerDoneTransfer,
    showGlobalSnackbar,
    calledFrom,
    isBPImport,
}) => {
    const classes = useStyles({ isBPImport: isBPImport })

    const onFilepickerDone = (pickerResponse: PickerResponse) => {
        handlePickerDoneTransfer?.(pickerResponse)

        if (pickerResponse.filesFailed.length) {
            showGlobalSnackbar?.({
                variables: {
                    id: `ImportFileUploadError`,
                    message: `Error uploading files`,
                    variant: 'error',
                },
            })
        }
    }
    const onFileSeleted = (file: PickerFileMetadata) => {
        if (!file.size || file.size === 0) {
            throw new Error(
                'File has 0KB size, upload file with more than 0KB size.'
            )
        }
    }
    return (
        <GridContainer
            data-testid="addFileWrapper"
            className={classes.fileStackWrapper}
        >
            <GridItem className={classes.uploadBtnContainer}>
                <FilestackButton
                    key="filestack-upload-button"
                    apiKey={processEnv.REACT_APP_FileStack_ApiKey}
                    pickerOptions={{
                        accept: ['docx', 'doc'],
                        maxFiles: 50,
                        maxSize: 10 * 1024 * 1024,
                        onFileSelected: onFileSeleted,
                    }}
                    kpCoreUri={processEnv.REACT_APP_KPCoreUri}
                    showGlobalSnackbar={showGlobalSnackbar!}
                    onFilepickerDone={onFilepickerDone}
                    handleLoadingFilestack={handleFilestackLoading}
                    renderButton={(
                        onClick: (event: React.MouseEvent) => void
                    ) => {
                        if (calledFrom === 'table') {
                            return (
                                <div className={classes.noData}>
                                    <RoundButton
                                        hasShadow={false}
                                        borderColor={
                                            isBPImport ? '#343A3F' : '#A78966'
                                        }
                                        backgroundColor={
                                            isBPImport ? '#343A3F' : '#A78966'
                                        }
                                        borderRadius={100}
                                        size="large"
                                        onClick={onClick}
                                    >
                                        Add Files
                                    </RoundButton>
                                </div>
                            )
                        } else if (calledFrom === 'image') {
                            return (
                                <map name="emptyImportMap">
                                    <area
                                        style={{ zIndex: 2, cursor: 'pointer' }}
                                        alt="Get started image button"
                                        onClick={onClick}
                                        coords="230,186,40"
                                        shape="circle"
                                    ></area>
                                </map>
                            )
                        } else {
                            return (
                                <Button
                                    variant="contained"
                                    data-testid="actionbar-import-button"
                                    color="default"
                                    disableElevation
                                    className={clsx(
                                        'btnViewAllProducts',
                                        classes.workspaceButton
                                    )}
                                    onClick={onClick}
                                >
                                    Add Files
                                </Button>
                            )
                        }
                    }}
                />
            </GridItem>
        </GridContainer>
    )
}
